import getFormType from './_form-type.js';

/**
 * GTM form validation with support for Gravity Forms. Checks forms for validation errors on page load
 * and upon form submission, pushing errors to the dataLayer for GTM tracking.
 * @exports
 */
export default function formFails() {
	/**
	* Retrieves a form's name, falling back to the Gravity Forms title if present, or a default name.
	* @param {HTMLFormElement} form - The form to retrieve the name for.
	* @returns {string} The name of the form.
	*/
	const getFormName = (form) => {
		if (form.closest('.gform_wrapper')) {
			const titleElement = form.querySelector('.gform_title');
			return titleElement ? titleElement.textContent.trim() : form.name || form.id || 'Unnamed Form';
		}
		return form.name || form.id || 'Unnamed Form';
	};

	/**
	* Collects all validation error messages from a form and returns them in an array.
	* @param {HTMLFormElement} form - The form element to check for validation errors.
	* @returns {string[]} An array of validation error messages.
	*/
	function getGformErrors(form) {
		// Array to hold error messages
		const errorMessages = [];

		// Select all elements with validation errors
		// For Gravity Forms, error messages are often indicated by '.gfield_error' class
		// Adjust the selector according to your form's error message structure
		const errorElements = form.querySelectorAll('.validation_message');

		// Iterate over error elements and extract messages
		errorElements.forEach((element) => {
			// Assuming the error message is the text content of the error element
			// Adjust this line if the structure of your error messages is different
			const errorMessage = element.textContent.trim();
			if (errorMessage) {
				const label = element.parentElement.querySelector('label')?.textContent;
				const legend = element.parentElement.querySelector('legend')?.textContent;
				errorMessages.push({ field: legend || label, message: errorMessage });
			}
		});

		return errorMessages;
	}

	/**
	* Collects all HTML5 validation error messages from a form and returns them in an array.
	* @param {HTMLFormElement} form - The form element to check for HTML5 validation errors.
	* @returns {string[]} An array of HTML5 validation error messages.
	*/
	function getHTML5FormErrors(form) {
		// Array to hold error messages
		const errorMessages = [];

		// Iterate over each form element
		for (const element of form.elements) {
			// Check if the element will validate and if it is currently invalid
			if (element.willValidate && !element.validity.valid) {
				// Add the validation message to the array
				errorMessages.push({ field: element.name, message: element.validationMessage });
			}
		}

		return errorMessages;
	}

	/**
	* Pushes form errors to the dataLayer based on a form element.
	* @param {HTMLFormElement} form - The form element to be checked for errors.
	*/
	const pushToDataLayer = (form) => {
		console.log('errors');
		const isGform = form.closest('.gform_wrapper');
		const formLocation = window.location.href; // Current URL
		const formElement = form.name || 'Unnamed Element'; // Name of the form element, if available
		const formName = getFormName(form);
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'form_fails',
			form_name: formName,
			form_location: formLocation,
			form_element: formElement,
			form_errors: isGform ? getGformErrors(form) : getHTML5FormErrors(form),
			form_type: getFormType(form),
		});
	};

	/**
	* Checks for Gravity Forms-specific validation errors and pushes them to the dataLayer if any are found.
	* @param {HTMLFormElement} form - The form element to be checked for Gravity Forms-specific validation errors.
	*/
	const checkGravityFormsErrors = (form) => {
		// Check for Gravity Forms specific validation errors
		const gravityErrors = form.querySelectorAll('.gfield_error');
		if (gravityErrors.length > 0) {
			pushToDataLayer(form);
		}
	};

	/**
	* Handles the form submission event. It differentiates between Gravity Forms and non-Gravity Forms,
	* applying appropriate validation checking.
	* @param {Event} event - The form submission event.
	*/
	const handleSubmit = (event) => {
		const form = event.target;
		if (!form.closest('.gform_wrapper')) {
			// For non-Gravity Forms, use HTML5 form validation
			pushToDataLayer(form);
		}
	};

	/**
	* Attaches a listener for the Gravity Forms 'gform_post_render' event to check for validation errors
	* on AJAX form submissions.
	*/
	jQuery(document).on('gform_post_render', (event, formId, currentPage) => {
		const form = document.getElementById(`gform_${formId}`);
		checkGravityFormsErrors(form);
	});

	/**
	* Attaches the submit event listener to each form on the page for handling form submissions.
	*/
	const forms = document.querySelectorAll('form');
	forms.forEach((form) => {
		form.addEventListener('submit', handleSubmit);
	});
}
